<template>
  <v-dialog
    v-model="localDialog"
    width="50%"
    @update:modelValue="updateDialog"
    transition=""
    persistent
    :open-delay="0"
    :close-delay="0"
  >
    <v-card>
      <v-card-title class="text-h6">
        <span>{{ isEntryMode ? "Add Project Budget Detail" : "Update Project Budget Detail" }}</span>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-form @submit.prevent="submit" ref="ProjectBudgetDetailsSetup">
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="project_id.value.value"
                :error-messages="project_id.errorMessage.value"
                clearable
                :items="getProjectList"
                :item-title="item => item ? `${item.code} - ${item.project_title}` : ''"
                autocomplete="off"
                item-value="id"
                @update:model-value="onProjectChange"
              >
                <template #label>
                  Project Name <span class="text-red-accent-4">*</span>
                </template>
              </v-autocomplete>

              <v-autocomplete
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="project_budget_id.value.value"
                :error-messages="project_budget_id.errorMessage.value"
                clearable
                :items="getFilteredProjectBudgetList"
                :item-title="item => item ? `${item.id} - ${item.project_name} (${item.project_code}) - ${formatCurrency(item.budget_amount)}` : ''"
                autocomplete="off"
                item-value="id"
                @update:model-value="onProjectBudgetChange"
              >
                <template #label>
                  Project Budget ID <span class="text-red-accent-4">*</span>
                </template>
              </v-autocomplete>

              <v-autocomplete
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="cost_head_id.value.value"
                :error-messages="cost_head_id.errorMessage.value"
                clearable
                :items="getCostHeadList"
                :item-title="item => item ? `${item.code} - ${item.name}` : ''"
                autocomplete="off"
                item-value="id"
              >
                <template #label>
                  Cost Head ID <span class="text-red-accent-4">*</span>
                </template>
              </v-autocomplete>

              <v-text-field
                v-model="planned_amount.value.value"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                type="number"
                step="0.01"
                :error-messages="planned_amount.errorMessage.value"
                clearable
                @input="calculateBalance"
              >
                <template #label>
                  Planned Amount <span class="text-red-accent-4">*</span>
                </template>
              </v-text-field>

              <v-text-field
                v-model="actual_amount.value.value"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                type="number"
                step="0.01"
                :error-messages="actual_amount.errorMessage.value"
                clearable
                @input="calculateBalance"
              >
                <template #label>
                  Actual Amount <span class="text-red-accent-4">*</span>
                </template>
              </v-text-field>

              <v-text-field
                v-model="balance_amount.value.value"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                :error-messages="balance_amount.errorMessage.value"
                readonly
              >
                <template #label>
                  Balance Amount
                </template>
              </v-text-field>

              <v-checkbox
                label="Active"
                v-model="status.value.value"
                :error-messages="status.errorMessage.value"
                density="compact"
                variant="outlined"
                hide-details
                single-line
                color="primary"
                value="1"
              ></v-checkbox>
              <input v-model="budget_detail_id.value.value" type="hidden" />
            </v-col>
          </v-row>
          <v-divider></v-divider>
          <v-card-actions>
            <v-layout class="justify-end">
              <v-btn
                class="bg-red"
                variant="flat"
                type="reset"
                @click="closeDialog"
                >Close</v-btn
              >
              <v-btn class="bg-brand w-15 ms-2" variant="flat" type="submit">
                {{ isEntryMode ? "Save" : "Update" }}
              </v-btn>
            </v-layout>
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, watch } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";

export default {
  name: "ProjectBudgetDetailsSetup",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({
        project_id: "",
        project_budget_id: "",
        cost_head_id: "",
        planned_amount: "",
        actual_amount: "",
        balance_amount: "",
        status: "1",
        budget_detail_id: null,
      }),
    },
  },
  data() {
    return {
      localDialog: this.dialog,
      selectedImage: null,
      errorMessages: "",
      loading: false,
      isEntryMode: true,
    };
  },
  computed: {
    getProjectList() {
      return this.$store.getters.getProjectList;
    },
    getProjectBudgetList() {
      return this.$store.getters.getProjectBudgetList;
    },
    getFilteredProjectBudgetList() {
      if (!this.project_id.value.value) return [];
      return this.getProjectBudgetList.filter(budget => budget.project_id == this.project_id.value.value);
    },
    getCostHeadList() {
      return this.$store.getters.getCostHeadList || [];
    },
  },
  watch: {
    dialog(val) {
      this.localDialog = val;
    },
    localDialog(val) {
      this.$emit("update:dialog", val);
    },
    data: {
      handler(newData) {
        this.isEntryMode = !newData.id;
        this.resetForm({ errors: {}, touched: {} });
        this.project_id.setValue(newData.project_id || "", false);
        this.project_budget_id.setValue(newData.project_budget_id || "", false);
        this.cost_head_id.setValue(newData.cost_head_id || "", false);
        this.planned_amount.setValue(newData.planned_amount || "", false);
        this.actual_amount.setValue(newData.actual_amount || "", false);
        this.balance_amount.setValue(newData.balance_amount || "", false);
        this.status.setValue(String(newData.status) || "1", false);
        this.budget_detail_id.setValue(newData.id || "", false);
      },
    },
  },
  created() {
    if (this.$store.getters.getProjectList.length === 0) {
      this.$store.dispatch("getProjectList");
    }
    if (this.$store.getters.getProjectBudgetList.length === 0) {
      this.$store.dispatch("getProjectBudgetList");
    }
    if (this.$store.getters.getCostHeadList.length === 0) {
      this.$store.dispatch("getCostHeadList");
    }
  },
  methods: {
    updateDialog(val) {
      this.$emit("update:dialog", val);
    },
    closeDialog() {
      this.$emit("update:dialog", false);
    },
    onProjectChange(projectId) {
      // Reset project budget when project changes
      this.project_budget_id.setValue("", false);
      this.calculateBalance();
    },
    onProjectBudgetChange(budgetId) {
      // Could add budget validation logic here
      this.calculateBalance();
    },
    calculateBalance() {
      const planned = parseFloat(this.planned_amount.value.value) || 0;
      const actual = parseFloat(this.actual_amount.value.value) || 0;
      const balance = planned - actual;
      this.balance_amount.setValue(balance.toFixed(2), false);
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount || 0);
    },
  },
  setup(props, { emit }) {
    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        project_id: yup.number().required().label("Project Name"),
        project_budget_id: yup.number().required().label("Project Budget ID"),
        cost_head_id: yup.number().required().label("Cost Head ID"),
        planned_amount: yup.number().required().min(0).label("Planned Amount"),
        actual_amount: yup.number().required().min(0).label("Actual Amount"),
        balance_amount: yup.number().label("Balance Amount"),
      }),
    });

    const project_id = useField("project_id");
    const project_budget_id = useField("project_budget_id");
    const cost_head_id = useField("cost_head_id");
    const planned_amount = useField("planned_amount");
    const actual_amount = useField("actual_amount");
    const balance_amount = useField("balance_amount");
    const status = useField("status");
    const budget_detail_id = useField("budget_detail_id");
    const errorMessages = ref("");
    const loading = ref("");
    const store = useStore();

    const submit = handleSubmit((values) => {
      store
        .dispatch("saveUpdateProjectBudgetDetail", values)
        .then((response) => {
          resetForm();
          emit("update:dialog", false);
          toast.success(response.data.message);
          loading.value = true;
          store
            .dispatch("getProjectBudgetDetailList")
            .then((data) => {
              loading.value = false;
            })
            .catch((error) => {
              loading.value = false;
              toast.error(error.response.data.message);
            });
        })
        .catch((error) => {
          toast.error(error.response.data.message);
        });
    });
    return {
      submit,
      project_id,
      project_budget_id,
      cost_head_id,
      planned_amount,
      actual_amount,
      balance_amount,
      status,
      errorMessages,
      budget_detail_id,
      resetForm,
      loading,
    };
  },
  mounted() {},
};
</script>